import {
  Box,
  Checkbox,
  createStyles,
  makeStyles,
  Popover,
  PopoverOrigin,
  Theme,
} from '@material-ui/core';
import { FilterOptionsState, Value } from '@material-ui/lab';
import {
  AutocompleteGetTagProps,
  AutocompleteRenderOptionState,
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import { Avatar } from 'copilot-design-system';
import React from 'react';

import Autocomplete from 'src/components/Autocomplete';
import { BaseActionsMenuProps } from 'src/constants';
import {
  Action,
  BaseActionsMenuButton,
  BaseMenu,
} from 'src/legacy/components/Dropdowns';
import { BaseMenuInput } from 'src/legacy/components/Dropdowns/BaseMenuInput';
import { PlusIcon } from 'src/legacy/components/Icons';
import { SelectOption } from 'src/legacy/components/Select/types';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import * as Colors from 'src/theme/colors';
import { DropdownMenuShadow } from 'src/theme/shadows';
import { getInitials } from 'src/utils/UserUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    popoverRoot: {
      border: `1px solid ${Colors.DividersAndCardBorders}`,
      boxShadow: DropdownMenuShadow,
    },
    paper: {
      margin: 0,
      borderRadius: 0,
      '& > ul': {
        padding: 0,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    option: {
      minHeight: 'auto',
      alignItems: 'center',
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px ${theme.spacing(
        1,
      )}px ${theme.spacing(1.5)}px`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      '&[data-focus="true"]': {
        backgroundColor: Colors.HoverBackground,
      },
      '&[aria-disabled="true"]': {
        opacity: 1,
        backgroundColor: 'transparent',
      },
    },
    popperDisablePortal: {
      position: 'relative',
    },
    iconSelected: {
      fontSize: 12,
      marginRight: theme.spacing(1.5),
      color: theme.palette.primary.main,
    },
    checkbox: {
      padding: `0 ${theme.spacing(1.5)}px 0 0`,
      '& .MuiSvgIcon-root': {
        fontSize: 14,
      },
      '&.Mui-disabled': {
        color: Colors.NonHoverBorder,
      },
    },
    listHeaderTitle: {
      marginLeft: theme.spacing(1.5),
      marginTop: theme.spacing(0.75),
      marginBottom: theme.spacing(0.25),
      color: Colors.LightGray,
    },
    avatarWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      marginRight: theme.spacing(1.5),
    },
  }),
);

export type PopoverCloseReason =
  | 'backdropClick'
  | 'escapeKeyDown'
  | 'toggleInput';
interface SelectDropDownProps<
  DropdownOption extends SelectOption = SelectOption,
  IsMultiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> {
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: (
    event: React.ChangeEvent<Record<string, unknown>>,
    reason: PopoverCloseReason,
  ) => void;
  noOptionsText?: string;
  placeholder: string;
  isMultiple: IsMultiple;
  onChange: (
    ev: React.ChangeEvent<Record<string, unknown>>,
    newValue: Value<
      DropdownOption | SelectOption,
      IsMultiple,
      DisableClearable,
      FreeSolo
    >,
  ) => void;
  width?: string | number;
  anchorOrigin?: PopoverOrigin;
  value?: Value<
    DropdownOption | SelectOption,
    IsMultiple,
    DisableClearable,
    FreeSolo
  >;
  options: (DropdownOption | SelectOption)[];
  getOptionDisabled?: (option: DropdownOption | SelectOption) => boolean;
  optionActions?: Action[] | null;
  defaultOptionIcon?: JSX.Element | null;
  creatable?: boolean;
  listHeaderTitle?: string;
  // since multi select custom field component has a different
  // way to render option items (tags) inside dropdown, we need
  // to override the autocomplete option renderer function
  // to match the desired ui.
  customOptionRenderer?: (
    option: DropdownOption | SelectOption,
    optionState: AutocompleteRenderOptionState,
  ) => void;

  inputRenderer?: (params: any) => React.ReactNode;
  transformOrigin?: PopoverOrigin;
  popoverClassname?: string;
  forcePopupIcon?: boolean;
  getOptionSelected?: (
    option: DropdownOption | SelectOption,
    value: DropdownOption | SelectOption,
  ) => boolean;
  renderTags?:
    | ((
        value: (DropdownOption | SelectOption)[],
        getTagProps: AutocompleteGetTagProps,
      ) => React.ReactNode)
    | undefined;
  disableClearable?: DisableClearable;
  ListboxComponent?: React.ComponentType<any> | undefined;
  ListboxProps?: object | undefined;
  inputValue?: string;
  onInputChange?: (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: string,
  ) => void;
}

export function SelectDropDown<
  DropdownOption extends SelectOption = SelectOption,
  IsMultiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  id,
  open,
  anchorEl,
  onClose,
  noOptionsText = 'No options',
  placeholder = '',
  isMultiple,
  value,
  onChange,
  width: menuWidth,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  getOptionDisabled,
  options,
  optionActions,
  creatable = false,
  defaultOptionIcon,
  listHeaderTitle,
  customOptionRenderer,
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  inputRenderer,
  popoverClassname,
  inputValue,
  onInputChange,
  ...autocompleteOptions
}: SelectDropDownProps<
  DropdownOption,
  IsMultiple,
  DisableClearable,
  FreeSolo
>) {
  const classes = useStyles();
  const [optionsActionMenuProps, setOptionsActionMenuProps] =
    React.useState<BaseActionsMenuProps | null>(null);

  const handleOpenActionsMenu = (
    menuOptions: BaseActionsMenuProps,
    optionId: string,
  ) => {
    setOptionsActionMenuProps({
      ...menuOptions,
      menuProps: {
        open: true,
        id: optionId,
        ...menuOptions.menuProps,
        onClose: () => setOptionsActionMenuProps(null),
        anchorOrigin: {
          vertical: 30,
          horizontal: 30,
        },
      },
    });
  };

  /**
   * This is the default option renderer
   * which render option items with avatar icons.
   * @param option
   * @param param1
   * @returns
   */
  const defaultOptionRenderer = (
    option: DropdownOption | SelectOption,
    { selected }: AutocompleteRenderOptionState,
  ) =>
    option.id === 'add-new-option-item' ? (
      <Box display="flex" alignItems="center" justifyContent="flex-start">
        <PlusIcon style={{ fontSize: 9 }} />
        <BaseTypography style={{ marginLeft: 8 }}>
          Create {option.label}
        </BaseTypography>
      </Box>
    ) : (
      <>
        {isMultiple && (
          <Checkbox
            checked={selected}
            name="selectAssignee"
            color="primary"
            disabled={getOptionDisabled && getOptionDisabled(option)}
            classes={{ root: classes.checkbox }}
          />
        )}
        <div className={classes.avatarWrapper}>
          {/* TODO: we unnaturally bend the types of `src/legacy/components/Select SelectOption` to
          account for the fact that selectdropdown has bespoke behavior here.
          we should remove this eventually */}
          {option.id !== 'no-option-item' ? (
            <Avatar
              src={option.avatarUrl ?? ''}
              fallbackColor={option.fallbackColor ?? ''}
              variant={option.type === 'company' ? 'rounded' : 'circle'}
              text={getInitials(option.label, {
                twoLetters: option.type !== 'company',
              })}
              size="sm"
              style={{
                ...(getOptionDisabled && getOptionDisabled(option)
                  ? { opacity: 0.5 }
                  : {}),
              }}
            />
          ) : (
            defaultOptionIcon
          )}
        </div>
        <Box
          display="flex"
          width={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <BaseTypography
            style={{
              ...(getOptionDisabled && getOptionDisabled(option)
                ? { color: Colors.LightGray }
                : {}),
            }}
          >
            {option.label}
          </BaseTypography>
          {optionActions && option.id !== 'no-option-item' && (
            <BaseActionsMenuButton
              actions={optionActions}
              onActionMenuClick={(menuOptions) =>
                handleOpenActionsMenu(menuOptions, option.id)
              }
            />
          )}
        </Box>
      </>
    );

  const filter = createFilterOptions<DropdownOption | SelectOption>();

  return (
    <Popover
      id={id}
      onClose={onClose}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      className={popoverClassname}
      classes={{
        paper: classes.popoverRoot,
      }}
    >
      <Box zIndex="modal">
        <Autocomplete<
          DropdownOption | SelectOption,
          IsMultiple,
          DisableClearable,
          FreeSolo
        >
          {...(creatable
            ? {
                filterOptions: (
                  opts: (DropdownOption | SelectOption)[],
                  params: FilterOptionsState<DropdownOption | SelectOption>,
                ) => {
                  // use trim to remove extra spaces so that
                  // we don't show create option button when
                  // user enter only spaces.
                  const trimmedInputValue = params.inputValue.trim();
                  let filtered = filter(opts, {
                    ...params,
                    inputValue: trimmedInputValue,
                  });
                  if (trimmedInputValue !== '') {
                    // find option that has the same input value as label
                    const exitingOption = filtered.find(
                      (o) =>
                        o.label.toLowerCase() ===
                        trimmedInputValue.toLowerCase(),
                    );

                    filtered = [
                      // add create new option item when typed value
                      // is not existing.
                      ...(!exitingOption
                        ? [
                            {
                              id: 'add-new-option-item',
                              label: trimmedInputValue,
                            },
                          ]
                        : []),
                      ...filtered,
                    ];
                  }

                  return filtered;
                },
              }
            : {})}
          inputValue={inputValue}
          onInputChange={onInputChange}
          open={open}
          onClose={(ev, reason) => {
            // since we're prevent autocomplete onClose event to be
            // fired when user clicks on menu option menu button.
            // we should only allow escape event.
            if (reason === 'escape') onClose(ev, 'escapeKeyDown');
          }}
          multiple={isMultiple}
          classes={{
            paper: classes.paper,
            option: classes.option,
            popperDisablePortal: classes.popperDisablePortal,
          }}
          // don't set value when not multiple because value should not show filled in input area
          value={value}
          onChange={onChange}
          getOptionLabel={(option) => option.label || ''}
          disablePortal
          renderTags={() => null}
          noOptionsText={noOptionsText}
          renderOption={customOptionRenderer || defaultOptionRenderer}
          getOptionDisabled={getOptionDisabled}
          renderInput={(params) => (
            <>
              {inputRenderer ? (
                inputRenderer(params)
              ) : (
                <BaseMenuInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={placeholder}
                  width={menuWidth}
                />
              )}

              {listHeaderTitle && (
                <BaseTypography
                  fontType="10Medium"
                  className={classes.listHeaderTitle}
                >
                  {listHeaderTitle}
                </BaseTypography>
              )}
            </>
          )}
          options={options}
          {...autocompleteOptions}
        />
      </Box>
      {optionsActionMenuProps && <BaseMenu {...optionsActionMenuProps} />}
    </Popover>
  );
}
